import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { isPresent } from 'ts-extras'
import type { IterableElement } from 'type-fest'
import { Actions } from '~/actions'
import Button from '~/components/Button'
import { Leaf, PaymentIcons, Stars } from '~/components/Icons'
import { useCountry, useTranslations } from '~/components/Locale'
import { Gallery } from '~/components/Photos'
import { DynamicPortal, Price, Promo } from '~/components/utils'
import { ProductDocument } from '~/gql'
import type { Nullable, Product } from '~/types'
import {
	cn,
	getPrice,
	getStoreCountry,
	getVariantImages,
	hasVariants,
	isError,
	isOk,
	parseNumber,
	shopifyFetch,
	toGraphId,
	useDispatch,
	useFreeShippingThreshold,
	useSelector,
	useWindowSize
} from '~/utils'
import { FunFacts } from './FunFacts'
import { InTheBox } from './InTheBox'
import { ProductExtra } from './ProductExtra'
import { SeedInfo } from './SeedInfo'
import { UpsellCard, Upsells } from './Upsells'
import { getAvailableProductOptions, Props as VariantProps, VariantSelector } from './VariantSelector'

export const ProductPage: React.FC = () => {
	const id = toGraphId(window.PRODUCT?.id || '', 'product')
	const response = useQuery([id], () => shopifyFetch(ProductDocument, { id })).data
	const upsells = useSelector(s => s.products.upsells)
	if (isError(response)) {
		console.error(response.errors)
		throw new Error('Failed to load product')
	}
	if (isOk(response) && upsells !== null) return <ProductPageComponent product={response.data} />
	else return null
}

interface Props {
	product: Product
}

export const ProductPageComponent: React.FC<Props> = ({ product }) => {
	const country = useCountry()
	const dispatch = useDispatch()
	const screen = useWindowSize()
	const pending = useSelector(s => s.cart.pendingOperation !== null)
	const [error, setError] = useState<Nullable<string>>(null)
	const [stage, setStage] = useState(-1)
	const [quantity, setQuantity] = useState(1)
	const upsells = useSelector(s => s.cart.stagedUpsells)

	const [selectedVariant, selectVariant] = useState<IterableElement<typeof product.variants>>()
	const variant = selectedVariant || product.variants[0]
	const handleStageChange: VariantProps['onChange'] = (v, [x]) => {
		if (x >= stage) setStage(x)
		selectVariant(v)
	}
	const productOptions = getAvailableProductOptions(product)
	const t = useTranslations('react', {
		COUNTRY: country.name,
		VARIANT_TYPE: productOptions[stage + 1]?.name.toLocaleLowerCase()
	})

	useEffect(() => {
		dispatch(Actions.loadUpsells(product, variant))
	}, [product, variant, dispatch])

	const settings = window.PRODUCT?.settings
	// Remove variant images from gallery thumbnails
	const variantImages = product.variants.flatMap(getVariantImages)
	const variantImagesIds = variantImages.map(img => img.id)
	const images = product.images.filter(img => !variantImagesIds.includes(img.id))
	const comparePrice = quantity * upsells.reduce((a, b) => a + getPrice(b.price), getPrice(variant.compareAtPrice))
	const price =
		quantity * upsells.reduce((a, b) => a + getPrice(b.price) - (b.discounts || 0), getPrice(variant.price))

	const outOfStock = !product.availableForSale
	const addToCartDisabled = pending || (hasVariants(product) && stage < productOptions.length - 1) || outOfStock

	const productUpsells = extractProductUpsells(product)
	const freeShippingThreshold = useFreeShippingThreshold()

	useEffect(() => setError(null), [addToCartDisabled])
	useEffect(() => {
		try {
			window.Klarna?.OnsiteMessaging?.refresh?.()
			// eslint-disable-next-line no-empty
		} catch {}
	}, [price])
	return (
		<>
			<div
				className={
					'mx-auto grid grid-cols-1 text-xl ' +
					'lg:grid-cols-[4fr,450px] xl:grid-cols-[4fr,3fr] ' +
					'3xl:grid-cols-[4fr,minmax(400px,2fr)] 2xl:grid-cols-[5fr,minmax(400px,3fr)] ' +
					'mb-12 gap-8 lg:mb-20 lg:mt-8 lg:px-8 xl:mt-10 xl:min-w-[88vw] xl:max-w-[160vh] xl:gap-16 xl:px-16'
				}
			>
				<div className="gallery relative min-h-[70vw] lg:sticky lg:top-10 lg:min-h-0 lg:self-start">
					<Gallery
						images={
							// Show featured image if a variant is not selected for Auk Mini
							selectedVariant || product.handle != 'auk-mini'
								? [...getVariantImages(variant), ...images]
								: [images[0], ...getVariantImages(variant), ...images.slice(1)]
						}
						mode={screen.width > 1024 ? 'both' : 'inline'}
						thumbnails={screen.width > 1024}
						className="safari-overflow-fix no-supports-aspect:lg:min-h-[60vh] lg:aspect-[12/8] lg:h-auto lg:rounded-3xl"
						preloadedImages={variantImages}
					/>
					<Promo
						promo={product}
						className={cn(
							'absolute left-0 top-0 rounded-br-lg bg-green-50 px-8 py-4 text-green-700 lg:top-8',
							'lg:left-[initial] lg:right-0 lg:rounded-l-xl lg:rounded-br-none'
						)}
					/>
				</div>
				<div className="mx-8 space-y-6 lg:mx-0">
					{getPrice(product.compareAtPriceRange.maxVariantPrice) > 0 && (
						<Price
							className="!-mt-3 block text-3xl font-bold lg:hidden"
							original={product.compareAtPriceRange.minVariantPrice}
							price={product.priceRange.minVariantPrice}
							asText
						/>
					)}
					<hgroup className="!mt-2 space-y-2 lg:!mt-0">
						{settings?.show_review_stars && product.reviews_average?.value && (
							<Stars rating={product.reviews_average?.value} count={product.review_count?.value} />
						)}
						<h1 className="mb-3 mt-0 flex items-center space-x-4 text-4xl sm:text-5xl">
							<span>{product.title}</span>
							{product.badge && (
								<span
									className={
										'whitespace-nowrap text-xl text-white ' +
										'xs:py-3 xs:px-6 space-x-2 rounded-full bg-green-600 px-4 py-2'
									}
								>
									<span className="font-semibold">{product.badge.value}</span>
									<Leaf className="align-middle" height="1.4em" />
								</span>
							)}
						</h1>
						{product.descriptionHtml.length > 11 && (
							<div
								className="text-xl font-normal"
								dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
							/>
						)}
					</hgroup>
					<SeedInfo product={product} />
					<FunFacts product={product} />
					{hasVariants(product) && (
						<VariantSelector stage={stage} product={product} onChange={handleStageChange} />
					)}
					<Upsells
						product={product}
						disabled={stage < productOptions.length - 1}
						onAdd={v => dispatch(Actions.addUpsell(v))}
						onRemove={v => dispatch(Actions.removeUpsell(v.id))}
						upsellIds={upsells.map(u => u.id)}
						selectedVariant={variant}
					/>
					{productUpsells && (
						<div>
							<legend className="mb-1 text-xl font-semibold">{t?.products.add_seeds}</legend>
							<ul className="list grid grid-cols-3 gap-4 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-4">
								{productUpsells.map(upsell => {
									const inCart = upsells.some(item => item.id === upsell.variants[0].id)
									return (
										<UpsellCard
											inCart={inCart}
											product={upsell}
											key={upsell.id}
											className="bg-gray-50"
											onClick={_ =>
												dispatch(
													inCart
														? Actions.removeUpsell(upsell.variants[0].id)
														: Actions.addUpsell(upsell.variants[0])
												)
											}
											title={inCart ? t?.actions.add_to_cart : 'Remove'}
										/>
									)
								})}
							</ul>
						</div>
					)}
					<div className="xs:text-3xl !mt-10 flex flex-col space-y-2 text-2xl">
						{comparePrice > price && (
							<strong className="text-e6/8 text-right leading-none text-green-700">
								{t?.checkout.you_save} {getPrice(comparePrice - price, true, 0)}
							</strong>
						)}
						<span className="flex justify-between">
							<span className="space-x-2 leading-none">
								<strong>{t?.checkout?.total}</strong>
								<span className="text-e5/8">
									{getPrice(variant.price) >= (freeShippingThreshold ?? 0)
										? getStoreCountry() === 'US'
											? t?.checkout.inc_shipping
											: t?.checkout.inc_shipping_and_taxes
										: getStoreCountry() === 'US'
										? null
										: t?.checkout?.inc_taxes}
								</span>
							</span>
							<Price
								className="text-right font-bold leading-none"
								original={
									comparePrice > price ? (
										<span className="text-e6/8">{getPrice(comparePrice, true, 0)}</span>
									) : null
								}
								price={getPrice(price, true, 0)}
							/>
						</span>

						{settings?.show_quantity_selector && (
							<label className="my-0 flex items-center rounded-full bg-gray-100 pl-8 text-2xl">
								<span className="shrink-0">{t?.auk_page.quantity}</span>
								<span className="mx-3">-</span>
								<input
									className="!my-0 !rounded-none !border-0 !bg-transparent !pl-0 text-inherit outline-none"
									type="number"
									min={1}
									defaultValue={quantity}
									inputMode="decimal"
									onChange={e => setQuantity(parseNumber(e.target.value) || 1)}
								/>
							</label>
						)}
						<Button
							size="xl"
							className="mt-2 uppercase"
							onClick={_ => {
								setError(null)
								dispatch(Actions.addToCart([variant, ...upsells].map(v => ({ id: v.id, quantity }))))
							}}
							onDisabledClick={_ =>
								!pending
									? outOfStock
										? setError(t?.auk_page.out_of_stock)
										: hasVariants(product)
										? setError(t?.auk_page.error_no_variant)
										: null
									: null
							}
							disabled={addToCartDisabled}
							title={
								hasVariants(product) && stage < productOptions.length - 1
									? t?.auk_page.error_no_variant
									: undefined
							}
						>
							{pending
								? t?.actions.add_pending
								: outOfStock
								? t?.auk_page.out_of_stock
								: t?.actions.add_to_cart}
						</Button>
						{error && <p className="text-16 !mb-0 font-bold text-red-600">{error}</p>}
					</div>

					<ProductExtra />
					{settings?.show_payment_icons && <PaymentIcons size="normal" className="justify-end" />}
				</div>
			</div>
			<style dangerouslySetInnerHTML={{ __html: '.main-content__wrapper { visibility: visible !important; }' }} />

			<DynamicPortal selector=".in-the-box">
				<InTheBox productId={product.id} upsells={upsells} />
			</DynamicPortal>
		</>
	)
}

function extractProductUpsells(product: Product) {
	if (product.upsells?.reference?.__typename !== 'Metaobject') return null
	const productRefs = product.upsells?.reference.products?.references
		?.map(u => (u?.__typename === 'Product' ? u : null))
		.filter(isPresent)
	return productRefs ?? null
}
